import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {Assignee} from './assignee'
import type {GraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {buildItemPickerSearchUrl} from '@github-ui/item-picker-base/api/urls'
import {SearchAssigneesInIssue, SearchAssigneesInRepo} from '@github-ui/server-persisted-queries'
import type {SearchAssigneesInIssueQuery} from '@github-ui/server-persisted-queries/types/SearchAssigneesInIssue'
import type {SearchAssigneesInRepoQuery} from '@github-ui/server-persisted-queries/types/SearchAssigneesInRepo'

export type GetSearchAssigneesRequest = {
  owner: string
  repo: string
  query?: string
  first?: number
  number?: number
  loginNames?: string
  capabilities?: string[]
}

type GetSearchAssigneesResponseData = SearchAssigneesInIssueQuery | SearchAssigneesInRepoQuery

export type GetSearchAssigneesResponse = GraphQLResponse<GetSearchAssigneesResponseData>

export async function apiGetSearchAssignees({
  owner,
  repo,
  query,
  first,
  number,
  loginNames,
  capabilities,
}: GetSearchAssigneesRequest): Promise<GetSearchAssigneesResponse> {
  const params: Record<string, unknown> = {
    owner,
    repo,
    queryId: number !== undefined ? SearchAssigneesInIssue : SearchAssigneesInRepo,
  }

  if (query) {
    params['query'] = query
  }

  if (first !== undefined) {
    params['first'] = first
  }

  if (number !== undefined) {
    params['number'] = number
  }

  if (loginNames) {
    params['loginNames'] = loginNames
  }

  if (capabilities && capabilities.length > 0) {
    params['capabilities'] = capabilities.join(',')
  }

  const requestUrl = buildItemPickerSearchUrl('assignees', params)
  const request = await verifiedFetchJSON(requestUrl)
  return (await request.json()) as GetSearchAssigneesResponse
}

const itemPickerQueryKeyBase = 'item_picker'

export function buildAssigneePickerSearchQueryKey(searchParams: GetSearchAssigneesRequest) {
  return [itemPickerQueryKeyBase, 'assignees', searchParams]
}

export type AssigneePickerQueryData = {
  assignees: Assignee[]
  totalCount?: number
}

export function transformSearchAssigneeResponseToQueryData(
  response: GetSearchAssigneesResponseData,
): AssigneePickerQueryData {
  if (!response.repository) {
    return {assignees: []}
  }

  if ('suggestedActors' in response.repository) {
    const nodes = response.repository.suggestedActors?.nodes ?? []
    return {
      assignees: nodes.filter(n => n != null),
      totalCount: response.repository.suggestedActors?.totalCount,
    }
  }

  if ('issueOrPullRequest' in response.repository) {
    const suggestedActors = response.repository.issueOrPullRequest?.suggestedActors
    const nodes = suggestedActors?.nodes ?? []
    return {
      assignees: nodes.filter(n => n != null),
      totalCount: suggestedActors?.totalCount,
    }
  }

  return {assignees: []}
}
